/**
 * services/UserService.ts
 * User account business logic and orchestration
 *
 * Handles user account lifecycle including invitation creation, activation,
 * and onboarding completion tracking for invitation-based authentication.
 *
 * Architecture: Service Layer (Business Logic)
 * - Orchestrates operations across repositories and other services
 * - No direct database access (uses usersRepository)
 * - Returns camelCase domain types
 * - Lets repository errors bubble up naturally
 */

import { supabase } from '@/lib/supabase';
import { usersRepository } from '@/repositories/usersRepository';
import { invitationTokensRepository } from '@/repositories/invitationTokensRepository';
import type {
  User,
  UserInsert,
  UserUpdate,
  UserStatus,
  UserInvitationStats
} from '@/types/supabase/User';

/**
 * User Service
 *
 * Provides business logic for user account management.
 * All database access delegated to usersRepository for clean separation of concerns.
 */
export class UserService {
  // ===== BASE CRUD OPERATIONS =====

  /**
   * Get user by ID
   *
   * @param id - User ID
   * @returns Promise resolving to user or null if not found
   */
  async get(id: string): Promise<User | null> {
    return usersRepository.getById(id);
  }

  /**
   * Get all users
   *
   * @returns Promise resolving to array of all users
   */
  async getAll(): Promise<User[]> {
    const { items } = await usersRepository.list();
    return items;
  }

  /**
   * Create new user
   *
   * @param data - User data to create
   * @returns Promise resolving to created user
   */
  async create(data: UserInsert): Promise<User> {
    return usersRepository.create(data);
  }

  /**
   * Update user
   *
   * @param id - User ID to update
   * @param changes - Partial user data to update
   * @returns Promise resolving to updated user
   */
  async update(id: string, changes: UserUpdate): Promise<User> {
    return usersRepository.update(id, changes);
  }

  /**
   * Delete user
   *
   * @param id - User ID to delete
   * @returns Promise that resolves when deletion is complete
   */
  async delete(id: string): Promise<void> {
    return usersRepository.delete(id);
  }

  // ===== SPECIALIZED QUERY METHODS =====

  /**
   * Get user by email address
   *
   * @param email - Email address to search for
   * @returns Promise resolving to user or null if not found
   */
  async getByEmail(email: string): Promise<User | null> {
    return usersRepository.getByEmail(email);
  }

  /**
   * Get users by status
   *
   * @param status - User status to filter by
   * @returns Promise resolving to users with specified status
   */
  async getByStatus(status: UserStatus): Promise<User[]> {
    const { items } = await usersRepository.list({ status });
    return items;
  }

  // ===== USER LIFECYCLE METHODS =====

  /**
   * Mark user as activated
   *
   * @param userId - ID of user to activate
   * @returns Promise resolving to updated user
   */
  async markAsActivated(userId: string): Promise<User> {
    const user = await usersRepository.update(userId, {
      status: 'active',
      activatedAt: new Date().toISOString()
    });

    // TODO: [ANALYTICS] Track user activation events (activation method, timing, completion rate)

    console.log('✅ UserService: User activated:', user.email);
    return user;
  }

  /**
   * Mark user onboarding as completed
   *
   * @param userId - ID of user who completed onboarding
   * @returns Promise resolving to updated user
   */
  async completeOnboarding(userId: string): Promise<User> {
    const user = await usersRepository.update(userId, {
      onboardingCompleted: true,
      onboardingCompletedAt: new Date().toISOString()
    });

    // TODO: [ANALYTICS] Track onboarding completion events (completion time, funnel analysis)

    console.log('✅ UserService: Onboarding completed for user:', user.email);
    return user;
  }

  /**
   * Disable user account
   *
   * Calls the disable-user Edge Function to:
   * - Set user status to 'disabled'
   * - Revoke all active sessions and refresh tokens
   * - Record audit trail (disabled_by, disabled_reason, disabled_at)
   * - Send email notification
   *
   * @param userId - ID of user to disable
   * @param reason - Reason for disabling the account
   * @returns Promise resolving to response with user and idempotent reason
   */
  async disableUser(userId: string, reason: string): Promise<{
    success: boolean;
    user: User;
    reason: 'disabled' | 'already_disabled';
    revocation_status?: 'success' | 'partial';
  }> {
    console.log(`🔒 UserService: Disabling user ${userId} - Reason: ${reason}`);

    // Get current session to pass auth token
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('No active admin session');
    }

    // Call disable-user Edge Function
    const { data, error } = await supabase.functions.invoke('disable-user', {
      body: { userId, reason },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    console.log('🔍 disable-user response:', { data, error });

    if (error) {
      console.error('❌ Edge Function error:', error);
      throw new Error(`Failed to disable user: ${error.message}`);
    }

    if (!data || !data.success) {
      console.error('❌ Edge Function returned unsuccessful response:', data);
      throw new Error(`Edge Function returned unsuccessful response: ${data?.error || 'Unknown error'}`);
    }

    console.log(`✅ UserService: User disabled successfully - ${data.reason}`);

    return data;
  }

  /**
   * Enable user account
   *
   * Calls the enable-user Edge Function to:
   * - Set user status to 'active'
   * - Clear disabled_* audit fields
   * - Record enabled_by and enabled_at
   * - Send email notification
   *
   * @param userId - ID of user to enable
   * @returns Promise resolving to response with user and idempotent reason
   */
  async enableUser(userId: string): Promise<{
    success: boolean;
    user: User;
    reason: 'enabled' | 'already_enabled';
  }> {
    console.log(`🔓 UserService: Enabling user ${userId}`);

    // Get current session to pass auth token
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('No active admin session');
    }

    // Call enable-user Edge Function
    const { data, error } = await supabase.functions.invoke('enable-user', {
      body: { userId },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });

    if (error) {
      throw new Error(`Failed to enable user: ${error.message}`);
    }

    if (!data.success) {
      throw new Error('Edge Function returned unsuccessful response');
    }

    console.log(`✅ UserService: User enabled successfully - ${data.reason}`);

    return data;
  }

  // ===== ADMIN STATISTICS METHODS =====

  /**
   * Get invitation and onboarding statistics for admin dashboard
   *
   * @returns Promise resolving to user statistics
   */
  async getInvitationStats(): Promise<UserInvitationStats> {
    // Get user stats from repository
    const { total, byStatus, onboardingCompleted } = await usersRepository.getStatusStats();

    // Get pending invitations count from repository
    const pendingInvitations = await invitationTokensRepository.countActive();

    const activeUsers = byStatus.active || 0;
    const disabledUsers = byStatus.disabled || 0;
    const onboardingCompletionRate = total > 0 ? Math.round((onboardingCompleted / total) * 100) : 0;

    const stats: UserInvitationStats = {
      totalUsers: total,
      pendingInvitations,
      activeUsers,
      disabledUsers,
      completedOnboarding: onboardingCompleted,
      onboardingCompletionRate
    };

    console.log('📊 UserService: Retrieved invitation stats:', stats);
    return stats;
  }

  // ===== INVITATION TOKEN INTEGRATION METHODS =====

  /**
   * DELETED: getByInvitationToken() - Legacy method removed in Task 5
   *
   * Invitation tokens moved to invitation_tokens table in October 2025 migration.
   * Use InvitationTokenService instead of TokenService for invitation flows.
   * Users don't exist until AFTER activation (auth.users trigger creates public.users).
   */

  /**
   * DELETED: activateByInvitationToken() - Legacy method removed in Task 5
   *
   * Invitation tokens moved to invitation_tokens table in October 2025 migration.
   * Activation now handled by AccountActivation component using InvitationTokenService.
   * Flow: validate token → create auth.users → trigger creates public.users → mark token used.
   */

  /**
   * REMOVED: createFromBetaApplicationWithInvitation
   *
   * Users are no longer created with 'invited' status. Instead:
   * 1. InvitationTokenService.createFromBetaApplication() creates invitation_token linked to beta_application
   * 2. User activates account via invitation link
   * 3. auth.users is created by Supabase Auth during activation
   * 4. public.users is auto-created by trigger
   * 5. invitation_token is marked as used and linked to new user
   *
   * See: InvitationTokenService.createFromBetaApplication()
   */

  /**
   * REMOVED: resendInvitation
   *
   * Invitation resending is now handled by InvitationTokenService since invitations
   * link to beta_applications (not users). Users don't exist until activation.
   *
   * See: InvitationTokenService.createFromBetaApplication() to regenerate invitation
   */

  /**
   * REMOVED: hasActiveInvitation
   *
   * Check invitation status using InvitationTokenService.getActiveTokenForApplication()
   * since invitations link to beta_applications (not users).
   */

  // ===== PASSWORD RESET METHODS =====

  /**
   * Send password reset email for a user (admin-initiated)
   *
   * @param userId - User ID to send password reset for
   * @param adminUsername - Admin username initiating the reset
   * @returns Promise resolving to password reset token info
   *
   * @throws Error - Always throws as feature is not yet implemented
   */
  async sendPasswordReset(_userId: string, _adminUsername?: string): Promise<{
    resetToken: string;
    resetExpires: string;
    emailSent: boolean;
    communicationId?: string;
  }> {
    // TODO: [FEATURE] Implement admin-initiated password reset
    //
    // CONTEXT: This method was broken during WU4 RLS hardening (Sprint 0 - Authentication & Email).
    // It previously tried to create tokens directly via TokenService, but RLS policies now prevent
    // authenticated users (including admins) from writing to the user_tokens table.
    // Only service_role (Edge Functions) can create tokens for security reasons.
    //
    // WHY IT BROKE:
    // - WU4 Task 2 added FORCE RLS to user_tokens table (migration 20251016214423)
    // - Policy: Only service_role has direct table access
    // - Authenticated users (including admins) get "permission denied" errors
    // - This is intentional security hardening to prevent token tampering
    //
    // IMPLEMENTATION PLAN:
    // 1. Call the existing request-password-reset Edge Function (same as user-initiated flow):
    //    ```typescript
    //    const { data, error } = await supabase.functions.invoke('request-password-reset', {
    //      body: {
    //        email: user.email,
    //        initiated_by: adminUsername  // Optional: add audit trail
    //      },
    //      headers: { Authorization: `Bearer ${session.access_token}` }
    //    });
    //    ```
    // 2. Edge Function validates admin is authenticated
    // 3. Edge Function inserts into password_reset_requests table (using service_role)
    // 4. Cron job (process-password-reset-queue) processes queue and sends email via Resend
    // 5. Optional: Add audit trail field to distinguish admin-initiated vs user-initiated
    //
    // RELATED FILES:
    // - Edge Function: supabase/functions/request-password-reset/index.ts
    // - Queue Processor: supabase/functions/process-password-reset-queue/index.ts
    // - RLS Policies: supabase/migrations/20251016214423_add_token_tables_rls_policies.sql
    // - Reference PROJECT_1C_AUTH_AND_EMAIL
    //
    // TEMPORARY WORKAROUND:
    // Ask the user to use the "Forgot Password" link on the login page.
    // The user-initiated flow works correctly via Edge Function.
    //
    // NOTE: Both callers (UserList.tsx:137, UserDetail.tsx:337) have try/catch error
    // handling that will display this error message in a toast notification.

    throw new Error(
      'Admin-initiated password reset is temporarily unavailable due to security improvements. ' +
      'Please ask the user to use the "Forgot Password" link on the login page.'
    );
  }

  /**
   * Update user's email address
   *
   * @param userId - User ID to update
   * @param newEmail - New email address
   * @returns Promise resolving to updated user
   */
  async updateUserEmail(userId: string, newEmail: string): Promise<User> {
    console.log(`🔄 UserService: Updating email for user ${userId} to ${newEmail}`);

    const updatedUser = await usersRepository.update(userId, { email: newEmail });

    // TODO: [ANALYTICS] Track email update events (update frequency, admin vs user initiated)

    console.log('✅ UserService: Email updated successfully for user:', updatedUser.email);
    return updatedUser;
  }
}

// Export service instance
export const userService = new UserService();
